import type { ClassValue } from "clsx";
import { clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * Custom typography utilities (src/typography-tokens.css) live under the `text-` prefix.
 * Registered as literal members of tailwind-merge's `font-size` group so that:
 * - `text-caption2` is not mistaken for a text color (the default text-color group ends
 *   in a catch-all validator) and survives `cn("text-caption2", "text-foreground")`;
 * - two typography classes merge last-wins, including against core sizes (`text-sm`);
 * - a later typography class drops an earlier `leading-*` (the utilities set line-height).
 *
 * MUST stay in sync with the `@utility text-*` names in src/typography-tokens.css —
 * a name missing here silently classifies as a text color again (and gets dropped
 * next to any color class). Guarded by src/lib/utils.test.ts.
 */
export const TYPOGRAPHY_SUFFIXES = [
	"hero",
	"display1",
	"display1-heavy",
	"display2",
	"display2-heavy",
	"display3",
	"display3-heavy",
	"display4",
	"display4-heavy",
	"title1",
	"title1-medium",
	"title2",
	"title2-medium",
	"headline",
	"headline-medium",
	"body1",
	"body1-medium",
	"body1-bold",
	"body2",
	"body2-medium",
	"body2-bold",
	"label1",
	"label1-medium",
	"label1-bold",
	"label1-tabular",
	"label1-tabular-medium",
	"label1-tabular-bold",
	"label2",
	"label2-medium",
	"label2-bold",
	"label2-tabular",
	"label2-tabular-medium",
	"label2-tabular-bold",
	"caption1",
	"caption1-medium",
	"caption1-bold",
	"caption1-tabular",
	"caption1-tabular-medium",
	"caption1-tabular-bold",
	"caption2",
	"caption2-medium",
] as const;

const twMerge = extendTailwindMerge({
	extend: {
		classGroups: {
			"font-size": [{ text: [...TYPOGRAPHY_SUFFIXES] }],
		},
	},
});

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}
